/**
 * 顶部导航栏（响应式）
 * Logo + 6个导航项 + 公司名下拉 + 返回官网
 * md 以下：汉堡菜单 + Drawer 抽屉导航
 * sm 以下：隐藏公司全名 / 隐藏"返回官网"文字
 */
import { useEffect, useState, MouseEvent } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Menu,
  MenuItem,
  Link,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MenuIcon from '@mui/icons-material/Menu';
import VerifiedIcon from '@mui/icons-material/Verified';
import EngineeringIcon from '@mui/icons-material/Engineering';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { useNavigate, useLocation } from 'react-router-dom';
import { TOP_NAV_ITEMS, OFFICIAL_WEBSITE_URL, ROUTE_PATHS } from '@/utils/constants';
import { COLORS, LAYOUT } from '@/theme/tokens';
import { HvuiCtLogo } from '@/components/icons/CustomIcons';
import { usePartnerStore } from '@/stores/partnerStore';
import { useAuthStore } from '@/stores/authStore';

/** 导航项图标映射 */
const NAV_ICONS: Record<string, React.ComponentType<any>> = {
  Verified: VerifiedIcon,
  Engineering: EngineeringIcon,
  Assignment: AssignmentIcon,
  TrendingUp: TrendingUpIcon,
  Inventory2: Inventory2Icon,
  RequestQuote: RequestQuoteIcon,
};

/**
 * TopNavBar 组件
 * 固定在页面顶部，高度 64px，白底，底部 1px 阴影分割线
 * md 以上：完整水平导航；md 以下：汉堡菜单 + Drawer
 */
export function TopNavBar(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const partnerInfo = usePartnerStore((state) => state.partnerInfo);
  const fetchPartnerInfo = usePartnerStore((state) => state.fetchPartnerInfo);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // 登录后顶栏挂载时强制刷新一次（确保最新公司名）
  useEffect(() => {
    if (isAuthenticated) {
      fetchPartnerInfo(true).catch(() => { /* 静默 */ });
    }
    // 只在登录态变化时触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  /** 判断导航项是否激活 */
  const isNavActive = (path: string): boolean => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  /** 处理导航项点击 */
  const handleNavClick = (path: string): void => {
    navigate(path);
    setDrawerOpen(false);
  };

  /** 打开公司名下拉菜单 */
  const handleMenuOpen = (event: MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  /** 关闭公司名下拉菜单 */
  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  /** 退出登录 */
  const handleLogout = async (): Promise<void> => {
    setAnchorEl(null);
    setDrawerOpen(false);
    const { logout } = useAuthStore.getState();
    await logout();
    navigate('/login');
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: COLORS.backgroundPaper,
        borderBottom: `1px solid ${COLORS.border}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}
    >
      <Toolbar
        sx={{
          height: { xs: '56px', md: `${LAYOUT.topNavHeight}px` },
          maxWidth: `${LAYOUT.maxContentWidth}px`,
          width: '100%',
          mx: 'auto',
          px: { xs: 1.5, sm: 2, md: `${LAYOUT.contentPadding}px` },
        }}
      >
        {/* 左侧 — 汉堡菜单按钮(md 以下显示) + Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{
              display: { xs: 'flex', md: 'none' },
              color: COLORS.textPrimary,
              padding: '6px',
            }}
          >
            <MenuIcon />
          </IconButton>
          <HvuiCtLogo />
        </Box>

        {/* 中间导航项 — md 以上显示 */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5, flex: 1, ml: 4 }}>
          {TOP_NAV_ITEMS.map((item) => {
            const isActive = isNavActive(item.path);
            const IconComp = NAV_ICONS[item.icon];
            return (
              <Box
                key={item.path}
                component="button"
                onClick={() => handleNavClick(item.path)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: isActive ? COLORS.primary : COLORS.textSecondary,
                  padding: '20px 8px',
                  position: 'relative',
                  transition: 'color 0.2s',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    color: COLORS.primary,
                  },
                  '&::after': isActive
                    ? {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        backgroundColor: COLORS.primary,
                        borderRadius: '2px',
                      }
                    : {},
                }}
              >
                {IconComp && (
                  <IconComp
                    sx={{
                      fontSize: 18,
                      color: isActive ? COLORS.primary : 'inherit',
                      opacity: isActive ? 1 : 0.7,
                      transition: 'all 0.2s',
                    }}
                  />
                )}
                {item.label}
              </Box>
            );
          })}
        </Box>

        {/* md 以下占位撑开 */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, flex: 1 }} />

        {/* 右侧操作区 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
          {/* 公司名称下拉 — sm 以上显示全名 */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
            <Button
              onClick={handleMenuOpen}
              endIcon={<ArrowDropDownIcon sx={{ color: COLORS.textDisabled, fontSize: 16 }} />}
              sx={{
                color: COLORS.textSecondary,
                fontSize: '14px',
                textTransform: 'none',
                minWidth: 'auto',
                maxWidth: { sm: '140px', md: 'none' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                '&:hover': { backgroundColor: 'transparent' },
              }}
            >
              {partnerInfo?.companyName ?? '合作伙伴'}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              sx={{ mt: 1 }}
            >
              <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}>账户设置</MenuItem>
              <MenuItem onClick={handleLogout}>退出登录</MenuItem>
            </Menu>
          </Box>

          {/* sm 以下显示头像式按钮 */}
          <Box
            onClick={handleMenuOpen}
            sx={{
              display: { xs: 'flex', sm: 'none' },
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: COLORS.appIconGradient,
              color: '#fff',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {partnerInfo?.companyName?.charAt(0) ?? '合'}
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            sx={{ mt: 1 }}
          >
            <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}>账户设置</MenuItem>
            <MenuItem onClick={handleLogout}>退出登录</MenuItem>
          </Menu>

          {/* 购物车角标按钮 */}
          {/* 返回官网 — sm 以上显示文字+图标，xs 仅图标 */}
          <Link
            href={OFFICIAL_WEBSITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: COLORS.textSecondary,
              fontSize: '14px',
              textDecoration: 'none',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              '&:hover': { color: COLORS.primary },
            }}
          >
            <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>返回官网</Box>
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        </Box>
      </Toolbar>

      {/* ===== 移动端 Drawer 导航 ===== */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: 260, pt: 2 },
        }}
      >
        <Box sx={{ px: 2, pb: 2 }}>
          <HvuiCtLogo />
        </Box>
        <Divider />
        <List>
          {TOP_NAV_ITEMS.map((item) => {
            const isActive = isNavActive(item.path);
            const IconComp = NAV_ICONS[item.icon];
            return (
              <ListItemButton
                key={item.path}
                onClick={() => handleNavClick(item.path)}
                sx={{
                  borderLeft: isActive
                    ? `3px solid ${COLORS.primary}`
                    : '3px solid transparent',
                  backgroundColor: isActive ? COLORS.primaryBg : 'transparent',
                  '&:hover': { backgroundColor: COLORS.primaryBg },
                }}
              >
                {IconComp && (
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      color: isActive ? COLORS.primary : COLORS.textSecondary,
                    }}
                  >
                    <IconComp sx={{ fontSize: 22 }} />
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '15px',
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? COLORS.primary : COLORS.textPrimary,
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => { setDrawerOpen(false); navigate('/settings'); }}
            sx={{ textTransform: 'none', mb: 1 }}
          >
            账户设置
          </Button>
          <Button
            fullWidth
            variant="text"
            onClick={handleLogout}
            sx={{ textTransform: 'none', color: COLORS.textSecondary }}
          >
            退出登录
          </Button>
        </Box>
      </Drawer>
    </AppBar>
  );
}
